import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from '../../i18n';
import NavBarNB from '../../components/NavBarNB';
import FooterNB from '../../components/FooterNB';
import { supabase } from '../../lib/supabase';
import { getErrorMessage } from '../../lib/errors';
import { useAuthStore } from '../../stores/authStore';
import { useToast } from '../../components/ToastNB';
import ButtonNB from '../../components/ui-nb/ButtonNB';
import CardNB from '../../components/ui-nb/CardNB';
import InputNB from '../../components/ui-nb/InputNB';
import { error as logError } from '../../lib/logger';

export default function JoinOrganizationPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { code: urlCode, lang } = useParams<{ code: string; lang: string }>();
  const locale = lang || 'en';
  const { user, signIn } = useAuthStore();
  const { addToast } = useToast();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [code, setCode] = useState('');

  // Pre-fill code from URL param or localStorage
  useEffect(() => {
    if (urlCode) {
      setCode(urlCode.toUpperCase());
    } else {
      // Check for pending invite code from auth redirect
      try {
        const pending = localStorage.getItem('tellowup-pending-invite-code');
        if (pending) {
          setCode(pending.toUpperCase());
          localStorage.removeItem('tellowup-pending-invite-code');
        }
      } catch {}
    }
  }, [urlCode]);

  // If the user is already authenticated and already has an organization,
  // they're on this page by mistake (stale QR, wrong account, etc.) —
  // send them to the dashboard instead of leaving them stuck on a join form
  // they have no reason to complete.
  useEffect(() => {
    if (user?.id && user.organizationId) {
      navigate(`/${locale}/admin`, { replace: true });
    }
  }, [user, locale, navigate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!code.trim()) {
      setError('Please enter the invite code');
      return;
    }

    if (!user?.id) {
      // Save code to localStorage before redirecting to login
      // so it survives the OAuth redirect
      if (code.trim()) {
        localStorage.setItem('tellowup-pending-invite-code', code.trim().toUpperCase());
      }
      window.location.href = `/${locale}/login`;
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Call the SECURITY DEFINER RPC that handles the full accept flow
      // server-side in a single transaction. This bypasses the RLS issue
      // where invitation_select (migration 038) hides invitations from
      // users whose profiles.organization_id is NULL.
      const { data, error: rpcError } = await supabase.rpc(
        'accept_invitation_code',
        { p_code: code.trim().toUpperCase() }
      );

      if (rpcError) throw rpcError;

      switch (data?.status) {
        case 'ok': {
          // Update auth store before redirect so org/role are available
          signIn({
            ...user,
            organizationId: data.organization_id,
            organizationName: data.organization_name || null,
            role: data.role,
          });

          const rolePath = data.role === 'admin' ? '/admin' : '/technician';
          navigate(`/${locale}${rolePath}`);
          return;
        }
        case 'not_found': {
          const msg = 'Invalid invite code. Please check and try again.';
          setError(msg);
          addToast(msg, 'error');
          return;
        }
        case 'expired': {
          const msg = 'This invitation has expired. Please ask your admin for a new one.';
          setError(msg);
          addToast(msg, 'error');
          return;
        }
        case 'used': {
          const msg = 'This invitation has already been used.';
          setError(msg);
          addToast(msg, 'error');
          return;
        }
        case 'email_mismatch': {
          const msg = 'This invitation was sent to a different email. Please contact your admin for a new one.';
          setError(msg);
          addToast(msg, 'error');
          return;
        }
        case 'already_in_org': {
          const msg = 'You are already a member of an organization.';
          setError(msg);
          addToast(msg, 'error');
          return;
        }
        case 'unauthenticated': {
          const msg = 'Session expired. Please login again.';
          setError(msg);
          addToast(msg, 'error');
          return;
        }
        case 'profile_missing': {
          const msg = 'User profile not found. Please contact support.';
          setError(msg);
          addToast(msg, 'error');
          return;
        }
        default: {
          const msg = data?.message ?? 'Failed to join organization';
          setError(msg);
          addToast(msg, 'error');
        }
      }
    } catch (err) {
      logError('Join org error', { error: getErrorMessage(err) });
      const message = getErrorMessage(err) || 'Failed to join organization';
      setError(message);
      addToast(message, 'error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-nb-cream">
      <NavBarNB />
      <main className="flex-1 max-w-md mx-auto px-4 py-16 text-center">
        <h1
          className="text-3xl font-bold text-black font-nb mb-2">
          {t('setup.joinOrg.title') || 'Join Your Team'}
        </h1>
        <p className="mb-8 text-nb-text-secondary">
          {t('setup.joinOrg.subtitle') || 'Enter the invite code from your employer'}
        </p>

        {error && (
          <CardNB className="mb-6 border-2 border-nb-red">
            <p className="text-nb-red text-sm">{error}</p>
          </CardNB>
        )}

        <form onSubmit={handleSubmit} className="space-y-6">
          <InputNB
            label={t('setup.joinOrg.codeLabel') || 'Invite Code'}
            value={code}
            onChange={(e) => setCode(e.target.value.toUpperCase())}
            placeholder={t('setup.joinOrg.codePlaceholder') || 'ABCD1234'}
            disabled={loading}
            className="text-center font-mono uppercase"
          />

          <ButtonNB variant="primary" type="submit" loading={loading} className="w-full">
            {loading
              ? (t('common.loading') || 'Loading...')
              : (t('setup.joinOrg.submit') || 'Join Team')}
          </ButtonNB>
        </form>

        <p className="mt-6 text-sm text-nb-text-muted">
          {t('setup.joinOrg.noCode') || "Don't have a code? "}
          <button
            onClick={() => navigate('/en/setup/role')}
            className="text-nb-black hover:underline">
            {t('setup.joinOrg.switchPath') || 'Create your own business instead'}
          </button>
        </p>
      </main>
      <FooterNB />
    </div>
  );
}
